import React, { useEffect, useState } from 'react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { Sparkles, PartyPopper, ShieldCheck, PiggyBank, Coins } from 'lucide-react';

/**
 * MarchettiBonusModal — celebratory reveal at the annual close when the
 * auto-provisioning (Midal perk) and/or the 25 % fiscal reserve
 * (Livret A) returned more cash than what was owed for IS.
 *
 * Mounts in App.js next to <PhaseNotification />. Reads `pendingMarchettiBonus`
 * from the store and dismisses via `dismissMarchettiBonus`.
 */
export default function MarchettiBonusModal() {
  const { t } = useTranslation();
  const pending = useGameStore(s => s.pendingMarchettiBonus);
  const dismiss = useGameStore(s => s.dismissMarchettiBonus);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    if (pending) {
      setReveal(false);
      const id = setTimeout(() => setReveal(true), 80);
      return () => clearTimeout(id);
    }
  }, [pending]);

  if (!pending) return null;

  const total = (pending.isOver || 0) + (pending.reserveRelease || 0);
  const hasIsOver = (pending.isOver || 0) > 0;
  const hasReserve = (pending.reserveRelease || 0) > 0;

  return (
    <div
      className="fixed inset-0 z-[105] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
      data-testid="marchetti-bonus-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className={`bg-ink-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border-2 border-emerald-500/40 transform transition-all duration-500 ${
          reveal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        data-testid="marchetti-bonus-modal"
      >
        {/* Confetti header (CSS only — six floating sparkles) */}
        <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700 px-6 py-7 text-center relative overflow-hidden">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <Sparkles
              key={`spark-${i}`}
              className={`absolute text-yellow-200 opacity-70 ${
                i % 2 === 0 ? 'w-5 h-5' : 'w-3 h-3'
              }`}
              style={{
                top: `${10 + (i * 11) % 70}%`,
                left: `${5 + (i * 17) % 90}%`,
                animation: `pulse 2.${i}s ease-in-out infinite`,
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-3 backdrop-blur-sm relative z-10">
            <PartyPopper className="w-9 h-9 text-yellow-200" />
          </div>
          <p className="text-yellow-200 text-[10px] uppercase tracking-widest font-mono font-bold mb-1 relative z-10">
            {t('marchetti_bonus_kicker', { year: pending.year || '' })}
          </p>
          <h2 className="text-2xl font-display font-black text-white relative z-10">
            {t('marchetti_bonus_title')}
          </h2>
          <p className="text-5xl font-mono font-black text-emerald-100 mt-3 tabular-nums relative z-10" data-testid="marchetti-bonus-total">
            +{formatMoney(total)}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <blockquote className="text-sm text-slate-200 leading-relaxed border-l-2 border-emerald-400 pl-3 italic">
            {t('marchetti_bonus_quote', { amount: formatMoney(total) })}
          </blockquote>

          <div className="space-y-2">
            {hasIsOver && (
              <div className="flex items-center gap-3 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="marchetti-bonus-line-is">
                <ShieldCheck className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-slate-300">{t('marchetti_bonus_is_over')}</p>
                </div>
                <span className="font-mono font-bold text-emerald-300 text-sm tabular-nums">+{formatMoney(pending.isOver)}</span>
              </div>
            )}
            {hasReserve && (
              <div className="flex items-center gap-3 p-2.5 rounded-lg bg-sky-500/10 border border-sky-500/30" data-testid="marchetti-bonus-line-reserve">
                <PiggyBank className="w-4 h-4 text-sky-300 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-slate-300">{t('marchetti_bonus_reserve_release')}</p>
                  {pending.interestEarned > 0 && (
                    <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <Coins className="w-3 h-3 text-amber-300" />
                      {t('marchetti_bonus_interest_included', { interest: formatMoney(pending.interestEarned) })}
                    </p>
                  )}
                </div>
                <span className="font-mono font-bold text-sky-300 text-sm tabular-nums">+{formatMoney(pending.reserveRelease)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={dismiss}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl text-sm font-bold transition-colors active:scale-[0.98]"
            data-testid="marchetti-bonus-dismiss"
          >
            {t('marchetti_bonus_cta')}
          </button>
        </div>
      </div>
    </div>
  );
}
